import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { TopBar } from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { ArrowRight, Upload, X, FileText, Plus, MagnifyingGlass, Package, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";

const MAX_ITEMS = 15;

export default function CreateBop() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", description: "", deadline: "", currency: "INR" });
  const [emailInput, setEmailInput] = useState("");
  const [emails, setEmails] = useState([]);
  const [pdf, setPdf] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  // Line items
  const [lineItems, setLineItems] = useState([]); // {item_number, item_desc, uom, forecast_qty}
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const searchTimer = useRef(null);

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    if (!searchOpen) return;
    searchTimer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get("/portal/items/search", { params: { q: searchQ, limit: 20 } });
        setSearchResults(data.items || []);
      } catch {
        setSearchResults([]);
      } finally { setSearching(false); }
    }, 200);
    return () => { if (searchTimer.current) clearTimeout(searchTimer.current); };
  }, [searchQ, searchOpen]);

  const addEmail = () => {
    const e = emailInput.trim().toLowerCase();
    if (!e) return;
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e)) {
      toast.error("Invalid email");
      return;
    }
    if (emails.includes(e)) return;
    setEmails([...emails, e]);
    setEmailInput("");
  };

  const addLineItem = (item) => {
    if (lineItems.find((li) => li.item_number === item.icode)) {
      toast.info(`Item ${item.icode} already added`);
      return;
    }
    if (lineItems.length >= MAX_ITEMS) {
      toast.error(`Maximum ${MAX_ITEMS} line items per BOP RFQ`);
      return;
    }
    setLineItems([
      ...lineItems,
      {
        item_number: item.icode,
        item_desc: item.iname,
        uom: item.unit || "",
        forecast_qty: "",
      },
    ]);
    setSearchOpen(false);
    setSearchQ("");
  };

  const removeLineItem = (idx) => setLineItems(lineItems.filter((_, i) => i !== idx));

  const updateQty = (idx, val) => {
    setLineItems(lineItems.map((li, i) => i === idx ? { ...li, forecast_qty: val } : li));
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPdf(data);
      toast.success("File uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setError("");
    if (emails.length === 0) { setError("Add at least one bidder email."); return; }
    if (lineItems.length === 0) { setError("Add at least one line item."); return; }
    for (const li of lineItems) {
      const q = Number(li.forecast_qty);
      if (!Number.isFinite(q) || q <= 0) {
        setError(`Forecast quantity for ${li.item_number} must be greater than 0.`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const payload = {
        type: "bop",
        title: form.title,
        description: form.description,
        deadline: form.deadline || null,
        requirement_path: pdf?.storage_path || null,
        requirement_filename: pdf?.original_filename || null,
        invited_emails: emails,
        currency: form.currency,
        line_items: lineItems.map((li) => ({
          item_number: li.item_number,
          item_desc: li.item_desc,
          uom: li.uom || null,
          forecast_qty: Number(li.forecast_qty),
        })),
      };
      const { data } = await api.post("/auctions", payload);
      toast.success("BOP RFQ created");
      navigate(`/auctions/${data.id}`);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="create-bop-page">
      <TopBar />
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-10 lg:py-14">
        <div className="text-[10px] tracking-[0.3em] uppercase text-neutral-500 mb-2 flex items-center gap-2">
          <Package size={12} weight="bold" /> New BOP RFQ
        </div>
        <h1 className="font-display font-extrabold text-4xl lg:text-5xl tracking-tightest leading-none mb-3">
          Procure bought-out parts
        </h1>
        <p className="text-sm text-neutral-600 mb-10 max-w-2xl">
          Bidders quote per line item. Evaluation is on the <strong>total bid value</strong> (sum of price × forecast qty across all items).
          Up to {MAX_ITEMS} line items per RFQ.
        </p>

        <form onSubmit={onSubmit} className="space-y-10">
          {/* TITLE + DESC + DEADLINE + CURRENCY */}
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="space-y-6">
              <Field label="Title" required>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required
                  placeholder="e.g. Q3 BOP — Brackets & Fasteners"
                  className="rounded-none border-black/20 h-11" data-testid="bop-title-input" />
              </Field>
              <Field label="Description" required>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} required rows={5}
                  placeholder="Scope, delivery location, quality requirements..."
                  className="rounded-none border-black/20" data-testid="bop-desc-input" />
              </Field>
              <Field label="Deadline (optional)">
                <Input type="datetime-local" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })}
                  className="rounded-none border-black/20 h-11" data-testid="bop-deadline-input" />
              </Field>
              <Field label="Currency">
                <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                  <SelectTrigger className="rounded-none border-black/20 h-11" data-testid="bop-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="INR">INR — Indian Rupee (₹)</SelectItem>
                    <SelectItem value="USD">USD — US Dollar ($)</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            </div>
            <div className="space-y-6">
              <Field label="Requirement Document (PDF, optional)">
                {!pdf ? (
                  <label className="border-2 border-dashed border-black/20 p-6 flex flex-col items-center justify-center text-center cursor-pointer hover:border-black transition-colors" data-testid="bop-upload-pdf-zone">
                    <Upload size={24} weight="bold" />
                    <div className="mt-2 text-sm font-medium">{uploading ? "Uploading..." : "Drop PDF or click to upload"}</div>
                    <input type="file" accept="application/pdf,image/*" hidden onChange={onUpload} disabled={uploading} />
                  </label>
                ) : (
                  <div className="border border-black/15 p-3 flex items-center gap-3" data-testid="bop-uploaded-pdf">
                    <FileText size={18} weight="bold" />
                    <div className="flex-1 text-sm truncate">{pdf.original_filename}</div>
                    <button type="button" onClick={() => setPdf(null)}><X size={14} weight="bold" /></button>
                  </div>
                )}
              </Field>
              <Field label="Invite Bidders (by email)" required>
                <div className="flex gap-2">
                  <Input type="email" value={emailInput} onChange={(e) => setEmailInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }}
                    placeholder="vendor@company.com"
                    className="rounded-none border-black/20 h-11" data-testid="bop-invite-email-input" />
                  <Button type="button" onClick={addEmail} className="rounded-none bg-[#0B1929] text-white h-11 px-4" data-testid="bop-invite-email-add">Add</Button>
                </div>
                {emails.length > 0 && (
                  <div className="flex flex-wrap gap-2 pt-2" data-testid="bop-invited-list">
                    {emails.map((e) => (
                      <span key={e} className="inline-flex items-center gap-2 border border-black/20 px-2.5 py-1 text-xs font-mono-plex">
                        {e}
                        <button type="button" onClick={() => setEmails(emails.filter((x) => x !== e))}><X size={12} weight="bold" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </Field>
            </div>
          </div>

          {/* LINE ITEMS */}
          <div>
            <div className="border-b border-black pb-3 mb-4 flex items-baseline justify-between">
              <div>
                <h2 className="font-display font-extrabold text-2xl tracking-tightest">Line Items</h2>
                <p className="text-xs text-neutral-500 mt-1">Search the portal item master, click to add, then enter forecast quantity.</p>
              </div>
              <span className="text-[10px] tracking-[0.25em] uppercase text-neutral-500">{lineItems.length} / {MAX_ITEMS}</span>
            </div>

            {/* Search */}
            <div className="relative mb-5" data-testid="item-search-container">
              <div className="flex items-center gap-2 border border-black/20 px-3 h-11 bg-white">
                <MagnifyingGlass size={14} weight="bold" className="text-neutral-500" />
                <input
                  type="text"
                  value={searchQ}
                  onFocus={() => setSearchOpen(true)}
                  onChange={(e) => { setSearchQ(e.target.value); setSearchOpen(true); }}
                  placeholder="Search by item code or description (e.g. WAS, BRACKET, BOLT)..."
                  className="flex-1 outline-none text-sm bg-transparent"
                  data-testid="item-search-input"
                />
                {searchOpen && (
                  <button type="button" onClick={() => { setSearchOpen(false); setSearchQ(""); }} className="text-xs uppercase tracking-[0.2em] font-semibold text-neutral-500" data-testid="item-search-close">
                    close
                  </button>
                )}
              </div>
              {searchOpen && (
                <div className="absolute z-30 left-0 right-0 mt-1 bg-white border border-black/20 max-h-[300px] overflow-y-auto shadow-sm" data-testid="item-search-results">
                  {searching ? (
                    <div className="p-3 text-xs uppercase tracking-[0.25em] text-neutral-500">Searching...</div>
                  ) : searchResults.length === 0 ? (
                    <div className="p-3 text-sm text-neutral-600">No matches. Try a different code or part of the description.</div>
                  ) : (
                    searchResults.map((it) => {
                      const already = lineItems.find((li) => li.item_number === it.icode);
                      return (
                        <button
                          key={it.icode}
                          type="button"
                          disabled={!!already}
                          onClick={() => addLineItem(it)}
                          className={`w-full text-left p-3 border-b border-black/5 flex items-center gap-3 hover:bg-neutral-50 ${already ? "opacity-40" : ""}`}
                          data-testid={`item-result-${it.icode}`}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-mono-plex font-semibold">{it.icode}</div>
                            <div className="text-xs text-neutral-600 truncate">{it.iname}</div>
                          </div>
                          <span className="text-[10px] tracking-[0.2em] uppercase border border-black/20 px-1.5 py-0.5 font-semibold">{it.unit || "—"}</span>
                          {already
                            ? <span className="text-[10px] tracking-[0.2em] uppercase text-neutral-500 font-semibold">Added</span>
                            : <Plus size={14} weight="bold" />}
                        </button>
                      );
                    })
                  )}
                </div>
              )}
            </div>

            {/* Selected items table */}
            {lineItems.length === 0 ? (
              <div className="border border-dashed border-black/15 p-8 text-center text-sm text-neutral-500" data-testid="empty-line-items">
                No line items yet. Use the search above to find and add items from the portal master.
              </div>
            ) : (
              <div className="border border-black/10" data-testid="line-items-table">
                <table className="w-full text-sm">
                  <thead className="bg-[#F8F9FA] border-b border-black/10">
                    <tr className="text-[10px] tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                      <th className="text-left p-3 w-12">#</th>
                      <th className="text-left p-3">Item</th>
                      <th className="text-left p-3 w-20">UOM</th>
                      <th className="text-right p-3 w-44">Forecast Qty *</th>
                      <th className="text-right p-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((li, i) => (
                      <tr key={li.item_number} className="border-b border-black/5" data-testid={`line-item-row-${li.item_number}`}>
                        <td className="p-3 font-mono-plex text-neutral-500">{i + 1}</td>
                        <td className="p-3">
                          <div className="font-mono-plex font-semibold">{li.item_number}</div>
                          <div className="text-xs text-neutral-600">{li.item_desc}</div>
                        </td>
                        <td className="p-3 text-xs">{li.uom || "—"}</td>
                        <td className="p-3">
                          <Input
                            type="number"
                            min="0"
                            step="any"
                            value={li.forecast_qty}
                            onChange={(e) => updateQty(i, e.target.value)}
                            placeholder="0"
                            className="rounded-none border-black/20 h-9 text-right font-mono-plex"
                            data-testid={`line-item-qty-${li.item_number}`}
                            required
                          />
                        </td>
                        <td className="p-3 text-right">
                          <button type="button" onClick={() => removeLineItem(i)} className="text-neutral-500 hover:text-red-600" data-testid={`remove-line-item-${li.item_number}`}>
                            <Trash size={14} weight="bold" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {error && <div className="text-sm text-red-600 border border-red-200 bg-red-50 p-3" data-testid="create-bop-error">{error}</div>}

          <Button
            type="submit"
            disabled={submitting}
            className="w-full lg:w-auto lg:px-12 rounded-none bg-[#22D3EE] hover:bg-[#0891B2] text-[#0B1929] font-semibold h-12 btn-sharp"
            data-testid="create-bop-submit"
          >
            {submitting ? "Creating..." : "Launch BOP RFQ"}
            <ArrowRight size={14} weight="bold" className="ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <div className="space-y-2">
    <Label className="text-[10px] tracking-[0.25em] uppercase font-semibold">
      {label}{required && <span className="text-red-600 ml-1">*</span>}
    </Label>
    {children}
  </div>
);
